import React, { Suspense, useEffect } from 'react'
import { Route, Switch, useLocation } from 'react-router'
import { action, when } from 'mobx'
import { observer } from 'mobx-react'
import loadable from '@loadable/component'
import pathParams, { pageNames } from 'src/routes/pathParams'
import {
  IS_BROWSER,
  application,
  bindBrowserScroll,
  setLocalStorage,
  getLocalStorage,
  deleteFromLocalStorage,
  checkMysiteOrPersonalOffer,
  getRedirectUrl,
  isGPSEnabled,
  getLiveEventStatus,
  enablePersonalOfferPDP,
  enablePORedesignFeature,
  enableHeaderMFE,
  enableHeaderMFESSR,
} from 'src/utils'
import { getPersonalOfferTrackingData } from 'src/utils/tracking'
import {
  // Header,
  Toast,
  AppLoader,
  ModalView,
  Overlay,
  KlarnaOverlay,
  ErrorBoundary,
  toastState,
} from 'src/views/components'
import { getBuyNowInPDP, enableExpressForRegistered } from 'config/appConfig'
import {
  getBuyNowResumeUrl,
  resumeBuyNowOnPageLanding,
} from 'src/utils/buyNowResumeUtils'
import NuskinHeader from 'src/views/components/Header/NuskinHeader'
import NuSkinFooter from 'src/views/components/NuSkinFooter'
import { PersonalHeaderWithRouter } from 'src/views/pages/PersonalHeader'
// import { getLocalStorage, setLocalStorage } from 'src/utils'
import { PersonalFooter } from 'src/views/pages/PersonalFooter'
import LiveEventHeader from 'src/views/components/Header/LiveEventHeader'
import LiveEventFooter from 'src/views/components/LiveEventFooter'
import { ScrollToTopWithRouter } from './ScrollToTop'
import {
  customerContainer,
  mysiteContainer,
  subscriptionContainer,
} from 'src/models'
import { UnSupported } from 'src/views/components/UnSupported/UnSupported'
import xss from 'xss'
import TagManager from 'react-gtm-module'
import {
  getPreferredLanguage,
  getLocaleCodeFromUrl,
} from 'src/utils/localeUtils'
import { CSWrapper } from 'src/views/components/CSWrapper/CSWrapper'
import {
  getMysiteSponsor,
  getMySiteUrlStatus,
  isEquinoxMysite,
  isEquinoxPOSite,
  getPoSponsorId,
} from 'src/utils/mySiteUtils'
import { MysitePreviewHeader } from 'src/views/components/Header'
import PersonalOfferPreviewHeader from 'src/views/components/Header/Components/PersonalOfferPreviewHeader'
import ProductTabCarousalContainer from 'src/views/components/Mysite/ProductTabCarousalContainer'
import { translationStore } from 'src/models/TranslationStore/TranslationStore'
import HeaderMFE from 'src/views/components/Header/HeaderMFE/HeaderMFE'
import GlobalModals from 'src/views/components/GlobalModals'
import AppInitializer from 'src/views/components/AppInitializer'

// enableHeaderMFE selects the Header MFE. enableHeaderMFESSR is an optional
// rendering capability for that selected header, not an independent rollout flag.
function isHeaderMfeEnabled() {
  return enableHeaderMFE()
}

// @Refer https://webpack.js.org/api/module-methods/
// const LoadableComponent = loadable(
//   props => import(`src/views/pages/${props.pageComponentName}`)
// )

const LoadableComponent = loadable(
  props => import(`src/views/pages/${props.pageComponentName}`)
)

function TrackPageViews() {
  let location = useLocation()
  const trackPageView = () => {
    let pageCategory =
      Object.keys(pageNames).find(
        key => pageNames[key] === location?.pathname
      ) || ''
    if (!pageCategory) {
      const splitPath = location?.pathname.split('/')
      pageCategory = splitPath.length > 1 ? splitPath[1] : ''
    } else {
      pageCategory = pageCategory
    }
    const language = getPreferredLanguage()
    const loginStatus = customerContainer?.isRegisterUser
      ? 'loggedIn'
      : 'loggedOut'
    const personalOfferData = getPersonalOfferTrackingData({
      includeBuyNow: false,
    })
    const contextValue = checkMysiteOrPersonalOffer()
    const isPersonalOfferPage =
      contextValue === 'personal_offer' ||
      location?.pathname?.includes('/personal-offer/')
    const locale = getLocaleCodeFromUrl() || ''
    const countryCode = locale.split('_')?.[1] || ''
    TagManager.dataLayer({
      dataLayer: {
        event: 'pageview',
        page_category: pageCategory,
        brand: 'nuskin',
        language: language,
        login_status: loginStatus,
        ...personalOfferData,
        ...(isPersonalOfferPage && { country_code: countryCode }),
      },
    })
  }
  useEffect(() => {
    if (IS_BROWSER) {
      translationStore.checkAndTriggerPageTranslation(true)
    }

    const isPDP = location.pathname.startsWith(pageNames.product)

    if (IS_BROWSER && !isPDP) {
      window.isCurrentConstructorUrl = undefined
    } else if (
      IS_BROWSER &&
      enablePersonalOfferPDP() &&
      location.pathname.includes('/personal-offer/') &&
      location.pathname.includes('/product/')
    ) {
      window.isCurrentConstructorUrl = undefined
    } else {
    }
    // queryparams
    trackPageView()

    resetInsider()

    // console.log('current page location is >>> ', { location })
    /**
     * @fix for NUSKIN-1109, PLT813-4813, PLT813-4815
     * after signin required to redirect to prev location
     * so saving the current page location at localStorage
     */
    if (
      IS_BROWSER &&
      !location.pathname.includes(pageNames.loginCallback) &&
      !location.pathname.includes(pageNames.logoutCallback)
    ) {
      let curPageUrl = `${location.pathname}${location.search}`
      const mysiteSponsor = getMysiteSponsor()
      const poSponsor = getPoSponsorId()
      const urlContainMysite = location.pathname?.includes('/mysite/')
      const urlContainPersonalOffer =
        location.pathname?.includes('/personal-offer/')

      if (isEquinoxMysite() && mysiteSponsor && !urlContainMysite) {
        curPageUrl = `/mysite/${mysiteSponsor}${location.pathname}${location.search}`
      } else if (isEquinoxPOSite() && poSponsor && !urlContainPersonalOffer) {
        const isLandingPage = location.pathname === '/'
        curPageUrl = `/personal-offer/${poSponsor}${isLandingPage ? location.search : location.pathname + location.search}`
      } else if (urlContainMysite && !isEquinoxMysite()) {
        curPageUrl = '/'
      }

      const prevPageUrl = getLocalStorage('curPageUrl') || ''
      handleExpressCheckoutRedirect(prevPageUrl, curPageUrl)
      if (curPageUrl !== prevPageUrl) {
        setLocalStorage('prevPageUrl', prevPageUrl)
        setLocalStorage('curPageUrl', curPageUrl)
        const isProfileRelatedPage =
          curPageUrl?.includes('myprofile') ||
          curPageUrl?.includes('accountprofile')
        if (!isProfileRelatedPage) {
          // check and reset session if not profile related page
          // for profile page, call is made on MyAccountSection.jsx componentDidMount
          customerContainer?.getProfile({ isToCheckSession: true })
        }
        if (prevPageUrl.includes(pageNames.myaccountsubscriptions)) {
          deleteFromLocalStorage('subscriptionActiveTab')
          deleteFromLocalStorage('scheduleOrderSelectedDate')
          deleteFromLocalStorage('subscriptionNextOrderSelectedDate')
          subscriptionContainer.subscriptionSelectedDate = ''
        }
      }
      checkAndShowCommonToast(location?.state)
    }
  }, [location])

  // Safety net for sign-up exits that bypass LoginCallback (JITSU MFE, native
  // /register): redirect the shopper back to the parked PDP once authenticated.
  useEffect(() => {
    if (!IS_BROWSER || !getBuyNowResumeUrl()) {
      return undefined
    }
    return when(() => customerContainer.isRegisterUser, resumeBuyNowOnPageLanding)
  }, [location])

  return <></>
}

/**
 * Handles the redirection logic for the express checkout page.
 * If the current page is not the express PDP page or if the user is not a registered user,
 * it removes the 'isExpressPDPLogin' flag from local storage.
 *
 * @param {string} [previousPageURL=''] - The URL of the previous page.
 * @param {string} [currentPageURL=''] - The URL of the current page.
 */
function handleExpressCheckoutRedirect(
  previousPageURL = '',
  currentPageURL = ''
) {
  if (getBuyNowInPDP()) {
    if (
      !currentPageURL.includes(pageNames.product) ||
      (currentPageURL.includes(pageNames.product) &&
        !customerContainer.isRegisterUser)
    ) {
      deleteFromLocalStorage('isExpressPDPLogin')
    }
  } else {
    if (
      !currentPageURL.includes(pageNames.expressPDP) ||
      (currentPageURL.includes(pageNames.expressPDP) &&
        !customerContainer.isRegisterUser)
    ) {
      deleteFromLocalStorage('isExpressPDPLogin')
    }
  }
}

/**
 * Checks for pending toasts and displays them after a route change.
 * Handles two sources:
 *  1. errorInfo in localStorage — legacy cross-page error bridge.
 *  2. pendingMfeToast in location.state — forwarded by the Checkout MFE's
 *     history wrapper before navigating out of the MFE context. The MFE's
 *     toastState is isolated (not shared via Module Federation), so this is
 *     the single generic handler for any MFE toast, present or future.
 *
 * @param {object} [locationState] - current React Router location.state
 */
export function checkAndShowCommonToast(locationState) {
  const errorInfo = getLocalStorage('errorInfo')
  if (errorInfo) {
    toastState?.setToastMessage(errorInfo, false)
    deleteFromLocalStorage('errorInfo')
  }

  const mfeToast = locationState?.pendingMfeToast
  if (mfeToast?.message) {
    toastState?.setToastMessage(mfeToast.message, mfeToast.successCondition)
  }
}

function renderRoutes(routes, locale) {
  // Create a new array instead of mutating the shared pathParams module reference.
  // The original unshift mutated the array on every render, causing route key
  // instability, component unmount/remount cycles, and infinite reload loops.
  const allRoutes = [
    { path: '/' + locale, pageComponentName: 'StudioPage' },
    ...routes,
  ]

  const sanitizeParams = routeProps => {
    let locationQueryString = routeProps?.location?.search || ''
    if (locationQueryString === '') {
      return locationQueryString
    }
    try {
      /**
       * @note fix for: [SECTEM-8208] security bug
       * where the URL can be encoded N number of times
       * and user can inject XSS in it
       * so, to avoid that decoding the url based on the count that user made
       *
       */
      const decodeAll = uri => {
        let decodedURL = uri
        while (decodedURL !== decodeURIComponent(decodedURL)) {
          decodedURL = decodeURIComponent(decodedURL)
        }
        return decodedURL
      }

      let decodeLocationQueryString = decodeAll(locationQueryString)
      let queryValues = decodeLocationQueryString.slice(1)
      let sanitizeValues = xss(queryValues, {
        whiteList: {},
        stripIgnoreTag: true, // filter out all HTML not in the whilelist
        stripIgnoreTagBody: ['script'],
      })

      return `?${sanitizeValues}`
    } catch (ex) {
      console.error(ex)
    }
    return locationQueryString
  }

  // Global Payment System - Start
  // Code to check if GPS payment is enabled and based on that update path
  const isGPSEnabledAndDefault = isGPSEnabled()
  let modifiedRoutes = allRoutes
  const isExpressRegisteredUser = enableExpressForRegistered()
  if (isGPSEnabledAndDefault) {
    const checkoutPath = isExpressRegisteredUser
      ? '/expressCheckout'
      : '/checkoutGps'
    pageNames.checkout = checkoutPath
    modifiedRoutes = allRoutes.map(route => {
      return route.path === '/checkout'
        ? { ...route, path: checkoutPath }
        : route
    })
  }
  // Global Payment System - End

  return modifiedRoutes.map((route, index) => {
    const { path, isExact = true, pageComponentName } = route
    const routeKey = route.key || `route-${index}`

    return (
      <Route
        key={routeKey}
        path={path}
        exact={isExact}
        strict
        render={routeProps => {
          // // Track why this render function is being called (browser only)
          // if (IS_BROWSER) {
          //   console.log('Loop debug [Route render called]', {
          //     routeKey,
          //     path,
          //     pageComponentName,
          //     routeProps,
          //     location: routeProps.location,
          //     history: routeProps.history,
          //     match: routeProps.match,
          //     stack: new Error().stack,
          //   })
          // }
          const locationQueryString = sanitizeParams(routeProps)
          const domainName = routeProps?.staticContext?.requestDomain || ''
          const queryParams = new URLSearchParams(locationQueryString?.slice(1))
          routeProps.location.queryParams = Object.fromEntries(
            queryParams?.entries()
          )
          routeProps.location.domainName = domainName

          if (IS_BROWSER) {
            const redirectUrl = getRedirectUrl({
              pageUrl: routeProps?.location?.pathname,
            })
            if (redirectUrl) {
              // Timeout keep the render pure function
              setTimeout(() => {
                routeProps?.history?.replace(redirectUrl)
              }, 200)
              return ''
            }

            if (typeof window?.ineum === 'function') {
              const pageName = route.pageComponentName
              if (route?.path == '/search/:searchTerm') {
                window.ineum('page', 'Search')
                // since pageComponentName is same for search and catalog
              } else {
                window.ineum('page', pageName)
              }
            }
          }

          return (
            <ErrorBoundary>
              <LoadableComponent {...routeProps} {...route} />
            </ErrorBoundary>
          )
        }}
      />
    )
  })
}

@observer
class Routes extends React.Component {
  constructor(props) {
    super(props)
  }

  enableLiveEvent = getLiveEventStatus()

  @action
  componentDidMount() {
    application.IsAppLoading = false
    bindBrowserScroll()
  }

  render() {
    let personalOffer
    let redirectToMySite
    let equinoxPersonalOffer
    let isToShowHomePageVideo = true
    // let shoppingCarts
    const locale = this.props.basename
    const isMySiteUrls = [
      pageNames.equinoxMysite,
      pageNames.signUp,
      pageNames.loginCallback,
      pageNames.viewCart,
      pageNames.checkout,
      pageNames.orderConfirmation,
      pageNames.myaccountsubscriptions,
      pageNames.klarnaCheckout,
      pageNames.expressCheckout,
      pageNames.personalOffer,
    ]
    const isValidMySiteUrl = getMySiteUrlStatus({ sync: true })

    let hasPreviewParam = false
    if (IS_BROWSER) {
      const urlParams = new URLSearchParams(window.location.search)
      hasPreviewParam = urlParams.has('preview')
    }

    try {
      if (typeof window !== 'undefined') {
        // const shoppingContext = localStorage.getItem('shoppingContext')
        // const shoppingContext = getShoppingContext()
        let userDetails = sessionStorage.getItem('storefront')
        if (userDetails) {
          userDetails = JSON.parse(userDetails)
        }
        let userId = userDetails?.sapId || ''

        let isMySite = isMySiteUrls.some(url => {
          if (location.pathname.includes(url)) {
            return true
          }
        })

        const contextValue = checkMysiteOrPersonalOffer()
        if (
          (contextValue === 'storefront' ||
            contextValue === 'personal_offer') &&
          isMySite
        ) {
          personalOffer = true
        }
        // Set redirectToMySite to true if MySite redesign feature is enabled and URL is valid
        if (isEquinoxMysite() && isValidMySiteUrl) {
          redirectToMySite = true
          personalOffer = false
          /* mysite admin can set whether they need homepage content to show or hide*/
          isToShowHomePageVideo =
            mysiteContainer.mysiteShopHeaderResponse?.market
              ?.showHomePageMarketingContent || false
        } else if (isEquinoxPOSite()) {
          equinoxPersonalOffer = true
          personalOffer = false
        }
      }
    } catch (e) {
      console.error('JSON.parse may failed >>> Routes', e)
    }

    // Mysite equinox URLs skip SSR unless Header MFE SSR is enabled — legacy header/footer
    // are not server-rendered on mysite. When enableHeaderMFESSR is on, mysite uses the
    // same header/footer shell as storefront; mysite-only chrome still hydrates on CSR.
    if (!IS_BROWSER && !enableHeaderMFESSR()) {
      const isMysiteUrl = isMySiteUrls.some(url =>
        this.props.urlWithoutLocale?.includes?.(url)
      )
      if (isMysiteUrl) {
        return <></>
      }
    }

    // Each region sits in its own <Suspense> so that a server/client markup
    // mismatch in one of them (header chrome, modals, footer…) only
    // client-renders that region. Without boundaries React 18 discards the
    // whole server-rendered tree on the first mismatch (recoverable error
    // #423), which re-creates the home-page MFE markup and moves the LCP.
    return (
      <React.Fragment>
        <Suspense fallback={null}>
          <Overlay position="fixed" textToShow="" />
          <KlarnaOverlay position="fixed"></KlarnaOverlay>
          <AppLoader />
          <Toast />
          <UnSupported />
          <ModalView />
        </Suspense>
        <Suspense fallback={null}>
          {this.enableLiveEvent !== 'true' ? (
            personalOffer ? (
              <PersonalHeaderWithRouter />
            ) : (
              <>
                {isEquinoxMysite() && isValidMySiteUrl && hasPreviewParam && (
                  <MysitePreviewHeader />
                )}
                {isEquinoxPOSite() &&
                  hasPreviewParam &&
                  enablePORedesignFeature() && <PersonalOfferPreviewHeader />}
                {isHeaderMfeEnabled() ? (
                  <HeaderMFE />
                ) : (
                  <NuskinHeader
                    isMySiteorPOSite={redirectToMySite || equinoxPersonalOffer}
                  />
                )}
                <GlobalModals />
                <div
                  className="navigationContainerItem"
                  tabIndex="-1"
                  style={{ outline: 'none' }}
                />
              </>
            )
          ) : (
            <LiveEventHeader />
          )}
        </Suspense>
        <Suspense fallback={null}>
          {redirectToMySite && <ProductTabCarousalContainer />}
          {isToShowHomePageVideo && <CSWrapper />}
        </Suspense>
        <section id="id-app-wrapper" className="app-wrapper position-relative">
          <Suspense fallback={null}>
            <ScrollToTopWithRouter>
              <AppInitializer />
              <TrackPageViews />
              <Switch>{renderRoutes(pathParams, locale)}</Switch>
            </ScrollToTopWithRouter>
          </Suspense>
        </section>
        <Suspense fallback={null}>
          {this.enableLiveEvent !== 'true' ? (
            personalOffer ? (
              <PersonalFooter />
            ) : (
              <NuSkinFooter />
            )
          ) : (
            <LiveEventFooter />
          )}
        </Suspense>
      </React.Fragment>
    )
  }
}

function resetInsider() {
  if (IS_BROWSER && typeof insider != 'undefined' && insider) {
    try {
      insider?.eventManager?.dispatch?.('init-manager:re-initialize')
    } catch (error) {
      console.error('Error resetting Insider:', error)
    }
  }
}
export { Routes, renderRoutes, isHeaderMfeEnabled }
export default Routes
